import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('sc_token'));
  const [loading, setLoading] = useState(true);

  // Create a stable axios instance with credentials
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      withCredentials: true,  // Enable httpOnly cookie support
    });
    
    // Add request interceptor to always include token header if available
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('sc_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []);

  useEffect(() => {
    // Try to authenticate - will work with either httpOnly cookie or localStorage token
    api.get('/auth/me')
      .then(res => { setUser(res.data); setLoading(false); })
      .catch(() => { 
        // Clear any stale localStorage token
        localStorage.removeItem('sc_token'); 
        setToken(null); 
        setUser(null); 
        setLoading(false); 
      });
  }, [api]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    // Store in localStorage for backward compatibility and request interceptor
    localStorage.setItem('sc_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, role) => {
    const res = await api.post('/auth/register', { name, email, password, role });
    // Store in localStorage for backward compatibility and request interceptor
    localStorage.setItem('sc_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = useCallback(async () => {
    try {
      // Call backend to clear httpOnly cookie
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors - just clear local state
    }
    localStorage.removeItem('sc_token');
    setToken(null);
    setUser(null);
  }, [api]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (e) {
      // Silent fail for refresh - user will need to re-login if session expired
      console.debug('User refresh failed:', e.message);
    }
  }, [api]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, api, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
